import { Injectable } from '@angular/core';
import { StorageService } from '../services';
import { ActivatedRouteSnapshot } from '@angular/router';
import { objectToCamel } from 'ts-case-convert';
import { IServiceInfoDetail, ITown } from 'src/app/models';
import { initialPaginationState } from '../store/initialisation/pagination';
import { ServiceInfoService } from '../services/service-info/service-info.service';
import { initialServiceInfoDetailsState } from '../store/initialisation/service-infos';
import {
  IServicesPanelFilters,
  deserializeServicesFilters,
  hasServicesPanelFilters,
  parseServicesTownsFromStorage,
  serializeServicesPanelFilters,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class ServiceInfosResolver {
  // Fingerprint of the inputs of the last successful list fetch. Skips redundant
  // fetches when navigating between /services and /services/:id (sibling routes
  // always re-run the resolver).
  private lastFetchFingerprint: string | null = null;
  // Fingerprint without page. Lets us tell a pure page change from a filter
  // change so the pagination only collapses to "currentPage ···" when totals
  // are genuinely unknown.
  private lastFilterFingerprint: string | null = null;
  // Discards stale responses so rapid searches can't apply out of order or wedge the skeleton.
  private requestSeq = 0;

  constructor(private serviceInfoService: ServiceInfoService, public storageService: StorageService) {}

  async resolve(route: ActivatedRouteSnapshot) {
    this.storageService.setRouteName('services')
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0)
      ? pageFromUrl
      : (route.data['services']?.pagination.actualPage || 1);
    const size = route.data['services']?.pagination.numberOfElements || 6;
    const search = route.queryParamMap.get('search') ?? undefined;
    const selectedTowns = await this.resolveTowns(route);
    const panelFilters = deserializeServicesFilters(route.queryParamMap);
    const panelFiltersKey = serializeServicesPanelFilters(panelFilters);

    const fingerprint = JSON.stringify({
      size,
      page,
      search: search ?? null,
      towns: selectedTowns.map((t) => t.id).sort(),
      panelFilters: panelFiltersKey,
    });
    if (fingerprint === this.lastFetchFingerprint && initialServiceInfoDetailsState.value?.length) {
      return;
    }

    initialServiceInfoDetailsState.isFetchingServiceInfos = true;
    initialServiceInfoDetailsState.value = [];
    const filterFingerprint = JSON.stringify({
      size,
      search: search ?? null,
      towns: selectedTowns.map((t) => t.id).sort(),
      panelFilters: panelFiltersKey,
    });
    if (filterFingerprint !== this.lastFilterFingerprint) {
      initialServiceInfoDetailsState.pagination.totalPages = 0;
      initialServiceInfoDetailsState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;

    const seq = ++this.requestSeq;
    if (search || hasServicesPanelFilters(panelFilters)) {
      this.resolveWithSearch(size, page, selectedTowns, search ?? '', fingerprint, seq, panelFilters);
    } else {
      this.resolveWithTowns(size, page, selectedTowns, fingerprint, seq);
    }
  }

  private async resolveTowns(route: ActivatedRouteSnapshot): Promise<ITown[]> {
    // URL is the source of truth whenever there is *any* query param. Storage is only
    // consulted on a truly bare URL (the "sidebar tab-switch" entry path).
    if (route.queryParamMap.keys.length > 0) {
      const cache = parseServicesTownsFromStorage(await this.storageService.getServicesAreas().catch(() => null));
      return deserializeServicesFilters(route.queryParamMap, cache).selectedTowns;
    }
    return parseServicesTownsFromStorage(await this.storageService.getServicesAreas().catch(() => null));
  }

  private async resolveWithSearch(
    size: number,
    page: number,
    towns: ITown[],
    search: string,
    fingerprint?: string,
    seq?: number,
    panelFilters?: IServicesPanelFilters,
  ) {
    try {
      const {
        content,
        number_of_elements,
        total_elements,
        total_pages,
        first,
        last,
      } = await this.serviceInfoService.getServiceInfosSearch(size, page, towns, search, panelFilters);
      if (this.isSuperseded(seq)) return;
      const result = this.getData(content, number_of_elements, total_elements, total_pages, first, last, page);
      if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
      return result;
    } catch (error) {
      return this.handleFetchError(error, seq);
    }
  }

  // Separate method for programmatic page loading (bypasses URL check)
  async resolveWithPage(page: number, route: ActivatedRouteSnapshot) {
    const storedAreas: any = await this.storageService.getServicesAreas().catch(() => []);
    const selectedTowns = storedAreas ? JSON.parse(storedAreas) : [];
    return this.resolveForSelection(route, page, selectedTowns);
  }

  async resolveForSelection(route: ActivatedRouteSnapshot, page: number, towns: ITown[]) {
    const search = route.queryParamMap.get('search') ?? '';
    const panelFilters = deserializeServicesFilters(route.queryParamMap);
    if (!search && !hasServicesPanelFilters(panelFilters)) return this.resolveWithTowns(6, page, towns);
    return this.resolveWithSearch(6, page, towns, search, undefined, undefined, panelFilters);
  }

  async resolveWithTowns(size: number, page: number, towns: ITown[], fingerprint?: string, seq?: number) {
    try {
      const {
        content,
        number_of_elements,
        total_elements,
        total_pages,
        first,
        last,
      } = await this.serviceInfoService.getServiceInfos(size, page, towns);
      if (this.isSuperseded(seq)) return;
      const result = this.getData(
        content,
        number_of_elements,
        total_elements,
        total_pages,
        first,
        last,
        page,
      );
      if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
      return result;
    } catch (error) {
      return this.handleFetchError(error, seq);
    }
  }

  private isSuperseded(seq?: number): boolean {
    return seq !== undefined && seq !== this.requestSeq;
  }

  // Awaited callers (no seq) keep the error; fire-and-forget fetches clear the skeleton instead of hanging.
  private handleFetchError(error: unknown, seq?: number): undefined {
    if (seq === undefined) throw error;
    if (seq === this.requestSeq) initialServiceInfoDetailsState.isFetchingServiceInfos = false;
    return undefined;
  }

  async getServiceInfos(
    route: ActivatedRouteSnapshot,
    searchValue?: string
  ) {
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0)
      ? pageFromUrl
      : (route.data['services']?.pagination.actualPage || 1);
    const size = route.data['services']?.pagination.numberOfElements || 6;

    const storedAreas: any = await this.storageService.getServicesAreas().catch(() => []);
    const selectedTowns = storedAreas ? JSON.parse(storedAreas) : [];

    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.serviceInfoService.getServiceInfosSearch(
      size,
      page,
      selectedTowns,
      searchValue,
      deserializeServicesFilters(route.queryParamMap),
    );
    return this.getData(
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
      page,
    );
  }

  getData(
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
  ) {
    const data = initialServiceInfoDetailsState;
    data.value = objectToCamel(content) as Array<IServiceInfoDetail>;
    data.isFetchingServiceInfos = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    return data;
  }
}